import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pause, Sparkles, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { formatNumber } from '@keywordgod/shared';
import { cn } from '@/lib/utils';

/**
 * 누적 키워드 카운터 — Landing(hero) / Dashboard(compact) 공용.
 *
 * 표시:
 *   "현재 누적된 키워드"
 *   <숫자>
 *   ⏸ 수집 일시정지 (paused 일 때만)
 *
 * 목표/진행률/Bar 는 표시하지 않는다 — 1,000만 도달 후에도 지속 수집되므로
 * "목표" 개념이 의미 없기 때문.
 *
 * hero 모드는 라이브 펄스 / 분당 증가 속도 / 카운트업으로 화려하게 표시.
 */
export function CollectedCounter({ size = 'compact' }: { size?: 'hero' | 'compact' }) {
  const q = useQuery({
    queryKey: ['stats', 'keywords'],
    queryFn: () => api.stats.keywords(),
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });

  const total = q.data?.total ?? 0;
  const paused = q.data?.paused ?? false;

  if (size === 'hero') {
    return <HeroCounter total={total} paused={paused} />;
  }
  return <CompactCounter total={total} paused={paused} />;
}

// ─── Compact (Dashboard 등) ─────────────────────────────────

function CompactCounter({ total, paused }: { total: number; paused: boolean }) {
  return (
    <div className="rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/5 via-gold/0 to-gold/5 px-5 py-4 text-center">
      <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-gray-400">
        현재 누적된 키워드
        {paused && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-300 normal-case">
            <Pause className="h-3 w-3" /> 일시정지
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl sm:text-3xl font-extrabold text-gold-gradient tabular">
        {formatNumber(total)}
      </div>
    </div>
  );
}

// ─── Hero (Landing) ─────────────────────────────────────────

function HeroCounter({ total, paused }: { total: number; paused: boolean }) {
  // 60초 윈도우 측정값 → 분당 처리 속도 계산
  const samplesRef = useRef<Array<{ ts: number; total: number }>>([]);
  const [ratePerMin, setRatePerMin] = useState(0);
  const [recentDelta, setRecentDelta] = useState(0);
  const prevTotalRef = useRef<number>(total);

  useEffect(() => {
    if (total <= 0) return;
    const now = Date.now();
    const samples = samplesRef.current;
    samples.push({ ts: now, total });
    while (samples.length > 0 && now - samples[0]!.ts > 60_000) samples.shift();

    if (samples.length >= 2) {
      const first = samples[0]!;
      const last = samples[samples.length - 1]!;
      const spanMs = Math.max(1000, last.ts - first.ts);
      const perMs = (last.total - first.total) / spanMs;
      setRatePerMin(Math.max(0, Math.round(perMs * 60_000)));
    }

    if (prevTotalRef.current && total > prevTotalRef.current) {
      const d = total - prevTotalRef.current;
      setRecentDelta(d);
      const id = window.setTimeout(() => setRecentDelta(0), 1200);
      prevTotalRef.current = total;
      return () => window.clearTimeout(id);
    }
    prevTotalRef.current = total;
  }, [total]);

  // 카운트업 애니메이션 (직전 값 → 현재 값을 약 600ms 에 걸쳐 보간)
  const displayed = useCountUp(total, 600);

  return (
    <div className="relative isolate overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-[#1a1408] via-[#0a0a0a] to-[#1a1408] px-6 py-10 sm:px-10 sm:py-14 text-center shadow-[0_0_60px_-15px_rgba(255,215,0,0.35)]">
      {/* 백그라운드 글로우 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute left-1/2 top-1/2 h-[480px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,215,0,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_30%,rgba(255,215,0,0.06)_50%,transparent_70%)]" />
      </div>

      {/* 상단 라이브 인디케이터 + 라벨 */}
      <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-gold/90">
        {paused ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-bold text-amber-300 normal-case tracking-normal">
            <Pause className="h-3.5 w-3.5" /> 수집 일시정지
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-emerald-300 normal-case tracking-normal font-semibold">LIVE</span>
          </span>
        )}
        <span className="h-3 w-px bg-gold/30" />
        <span>현재 누적된 키워드</span>
        <Sparkles className="h-3.5 w-3.5 text-gold animate-pulse" />
      </div>

      {/* 메인 카운터 */}
      <div className="relative mt-5">
        <div
          className={cn(
            'text-5xl sm:text-7xl md:text-8xl font-black tracking-tight tabular',
            'bg-gradient-to-r from-[#FFE066] via-[#FFD700] to-[#FFA500] bg-clip-text text-transparent',
            'drop-shadow-[0_0_25px_rgba(255,215,0,0.45)]',
          )}
        >
          {formatNumber(displayed)}
        </div>

        {/* 직전 증가량 토스트 (+N) */}
        {recentDelta > 0 && (
          <div
            key={`delta-${recentDelta}-${Date.now()}`}
            className="pointer-events-none absolute right-1/2 top-0 translate-x-[140px] sm:translate-x-[200px] animate-rise text-sm sm:text-base font-bold text-emerald-300 drop-shadow-[0_0_8px_rgba(110,231,183,0.6)]"
          >
            +{formatNumber(recentDelta)}
          </div>
        )}
      </div>

      <div className="mt-2 text-[11px] uppercase tracking-widest text-gray-500">
        개의 키워드를 분석할 준비가 되어있어요
      </div>

      {/* 하단 stats */}
      <div className="mt-7 inline-flex items-center gap-6 sm:gap-10 rounded-full border border-gold/20 bg-black/40 px-6 py-3 text-xs sm:text-sm">
        <Stat
          icon={<TrendingUp className="h-4 w-4 text-emerald-300" />}
          label="분당 수집"
          value={paused ? '—' : `+${formatNumber(ratePerMin)}`}
          accent="emerald"
        />
        <span className="h-6 w-px bg-gold/20" />
        <Stat
          icon={<Sparkles className="h-4 w-4 text-gold" />}
          label="자동 24시간 가동"
          value="ON"
          accent="gold"
        />
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: 'emerald' | 'gold';
}) {
  return (
    <div className="flex items-center gap-2.5">
      {icon}
      <div className="text-left">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
        <div
          className={cn(
            'tabular font-bold leading-tight',
            accent === 'emerald' ? 'text-emerald-300' : 'text-gold',
          )}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ─── useCountUp ─────────────────────────────────────────────

function useCountUp(target: number, durationMs: number): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === value) return;
    fromRef.current = value;
    startRef.current = null;
    const from = fromRef.current;
    const delta = target - from;
    if (Math.abs(delta) > 5000) {
      // 너무 큰 점프(초기 로딩 등)는 애니메이션 생략
      setValue(target);
      return;
    }

    const step = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + delta * eased));
      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(step);
      }
    };
    rafRef.current = window.requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return value;
}
